import { useServiceSubjectsHost } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/host';
import { isFaultLikeServiceSubject } from '@euler/functions/isFaultLikeServiceSubject';
import {
  ConstructionServiceSubject,
  FaultLikeServiceSubject,
  ServiceSubject,
} from '@euler/model';
import { ServiceSubjectType } from '@euler/model/enum';
import { withConfirmation } from '@euler/utils';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import { makeUniqueIdAlphabetic } from '@euler/utils/id';
import { getPyInitials } from '@euler/utils/pinyin/getPyInitials';
import { useNavigation } from '@react-navigation/native';
import { nanoid } from 'nanoid/non-secure';
import {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { Subject } from 'rxjs';
import { serviceSubjectItemKeyExtractor } from '../functions/serviceSubjectItemKeyExtractor';
import { usePredefinedConstructionItems } from '../functions/usePredefinedConstructionItems';
import { useVehicleInspectionSubjects } from '../functions/useVehicleInspectionSubjects';
import { useVehiclePendingIssues } from '../functions/useVehiclePendingIssues';
import {
  ServiceSubjectCategory,
  ServiceSubjectGroup,
  ServiceSubjectItem,
} from '../types';
import { CustomServiceSubjectBottomSheetModal } from './CustomServiceSubjectBottomSheetModal';
import {
  SelectedServiceSubjectsBottomSheet,
  SelectedServiceSubjectsBottomSheetAction,
} from './SelectedServiceSubjectsBottomSheet';

export type ServiceSubjectsContextProps = {
  categories: ServiceSubjectCategory[];
  serviceSubjectItemMap: Map<string, ServiceSubjectItem>;
  categoryMapById: Map<string, ServiceSubjectCategory>;
  groupMapById: Map<string, ServiceSubjectGroup>;
  addServiceSubject: (subject: ServiceSubject) => void;
  updateServiceSubject: (
    subject: FaultLikeServiceSubject,
    updates: { name: string; note?: string },
  ) => void;
  removeServiceSubject: (index: number) => void;
};

export const ServiceSubjectsContext =
  createContext<ServiceSubjectsContextProps>(null as any);

export const useServiceSubjectsContext = () =>
  useContext(ServiceSubjectsContext);

export const ServiceSubjectsContextProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const navigation = useNavigation();
    const {
      inspectionTemplates: templates,
      updateDetail,
      orderDetail$,
      showCustomServiceSubjectModal$,
      validateServiceSubjects,
      jobAssignmentEnabled,
    } = useServiceSubjectsHost();
    const { state } = usePredefinedConstructionItems();
    const constructionWorkItemCategories = state?.categories;
    const vehicleInspectionSubjects = useVehicleInspectionSubjects();
    const pendingIssues = useVehiclePendingIssues();

    const selectedSubjectsModalAction$ = useCreation(
      () => new Subject<SelectedServiceSubjectsBottomSheetAction>(),
      [],
    );

    const onDone = usePersistFn(async () => {
      // When assignment is disabled (e.g. the reservation item picker), items
      // carry no assignee by design — skip the "assign now?" prompt and leave.
      // Otherwise, if some subjects lack an assignee, offer to assign them.
      if (
        jobAssignmentEnabled !== false &&
        orderDetail$.value.serviceSubjects?.some(x => !x.assignedTo)
      ) {
        if (
          await withConfirmation({
            title: '提示',
            message:
              '有服务项目尚未派工，建议您为所有服务项目派工，是否现在派工? ',
          })
        ) {
          selectedSubjectsModalAction$.next({ type: 'reveal' });
          return;
        }
      }

      // otherwise, go back directly.
      navigation.goBack();
    });

    const addServiceSubject = usePersistFn((subject: ServiceSubject) => {
      updateDetail(draft => {
        if (subject.type === ServiceSubjectType.Construction) {
          // there might be multiple construction items with the same name
          // but come from different sources (pending issues, construction work items)
          // try to merge them if necessary
          const existing = draft.serviceSubjects?.find(
            x =>
              x.type === ServiceSubjectType.Construction &&
              x.name === subject.name,
          ) as ConstructionServiceSubject | undefined;
          if (existing) {
            Object.assign(existing, {
              pendingIssueId:
                subject.pendingIssueId ?? existing?.pendingIssueId,
              procedures: subject.procedures ?? existing?.procedures,
              assignedTo: subject.assignedTo ?? existing?.assignedTo,
            });
            return;
          }
        }
        if (!draft.serviceSubjects) draft.serviceSubjects = [];
        draft.serviceSubjects.push(subject);
      });
    });

    const updateServiceSubject = usePersistFn(
      (
        subject: FaultLikeServiceSubject,
        updates: { name: string; note?: string },
      ) => {
        const index = orderDetail$.value.serviceSubjects?.findIndex(
          x => isFaultLikeServiceSubject(x) && x.name === subject.name,
        );
        if (index == null || index < 0) return;
        updateDetail(draft => {
          const target = draft.serviceSubjects?.[index];
          if (target != null && isFaultLikeServiceSubject(target)) {
            if (!target.id) {
              target.id = makeUniqueIdAlphabetic();
            }
            target.name = updates.name;
            target.note = updates.note;
          }
        });
      },
    );

    const removeServiceSubject = usePersistFn((index: number) => {
      updateDetail(draft => {
        const serviceSubject = draft.serviceSubjects?.[index];
        if (!serviceSubject) return;
        draft.serviceSubjects?.splice(index, 1);
      });
    });

    const { categories, serviceSubjectItemMap, categoryMapById, groupMapById } =
      useMemo(() => {
        const list: ServiceSubjectCategory[] = [];
        const map = new Map<string, ServiceSubjectItem>();
        const categoryMap = new Map<string, ServiceSubjectCategory>();
        const groupMap = new Map<string, ServiceSubjectGroup>();

        const makeGroup = (
          categoryId: string,
          title: string,
          showItemNumber: boolean,
          itemBuilder: (
            groupId: string,
            addItem: (item: ServiceSubjectItem) => void,
          ) => void,
          groupBuilder?: (group: ServiceSubjectGroup) => void,
        ) => {
          const groupId = nanoid();
          const group: ServiceSubjectGroup = {
            id: groupId,
            categoryId,
            title,
            items: [],
            showItemNumber,
          };
          itemBuilder(groupId, item => {
            group.items.push(item);
            const key = serviceSubjectItemKeyExtractor(item);
            map.set(key, item);
          });
          groupBuilder?.(group);
          groupMap.set(groupId, group);
          return group;
        };

        // default category for inspection or repair
        const defaultCategoryId = nanoid();
        const defaultCategory: ServiceSubjectCategory = {
          id: defaultCategoryId,
          type: 'inspection-or-repair',
          name: '检查/检修',
          groups: [
            makeGroup(
              defaultCategoryId,
              '检测模板',
              false,
              (groupId, addItem) => {
                templates.forEach(template => {
                  addItem({
                    groupId,
                    type: ServiceSubjectType.Inspection,
                    title: template.name,
                    pyInitials: getPyInitials(template.name),
                    iconUrl:
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      template.icon ||
                      'https://img.zhichetech.com/files/template/icons/d2425bfb7369a39e5275dd00095c5116.png#UCEV%2C9%25N00IUM_%25NWAIU00M%7B~qt7-%3DIUa~%25M',
                    description: template.description ?? undefined,
                    template,
                  });
                });
              },
            ),
            makeGroup(
              defaultCategoryId,
              '遗留项目',
              true,
              (groupId, addItem) => {
                pendingIssues.forEach(issue => {
                  addItem({
                    groupId,
                    type: ServiceSubjectType.Construction,
                    title: issue.issue.name,
                    pendingIssue: issue,
                    pyInitials: getPyInitials(issue.issue.name),
                  });
                });
              },
            ),
            makeGroup(
              defaultCategoryId,
              '故障检修',
              true,
              (groupId, addItem) => {
                vehicleInspectionSubjects.forEach(subject => {
                  addItem({
                    groupId,
                    type: ServiceSubjectType.Fault,
                    title: subject.name,
                    showTypeLabel: true,
                    pyInitials: subject.pyInitials,
                    subject,
                  });
                });
              },
            ),
          ],
        };
        list.push(defaultCategory);
        categoryMap.set(defaultCategoryId, defaultCategory);

        for (const constructionCategory of constructionWorkItemCategories ??
          []) {
          const categoryId = nanoid();
          const category: ServiceSubjectCategory = {
            id: categoryId,
            type: 'construction',
            name: constructionCategory.name,
            categoryId: constructionCategory.id || nanoid(),
            template: constructionCategory.template,
            groups: [],
          };
          for (const group of constructionCategory.groups) {
            category.groups.push(
              makeGroup(
                categoryId,
                group.name,
                true,
                (groupId, addItem) => {
                  group.items.forEach(item => {
                    addItem({
                      groupId,
                      type: ServiceSubjectType.Construction,
                      title: item.name,
                      pyInitials: item.pyInitial ?? getPyInitials(item.name),
                      key: `workitem:${item.id}`,
                      template: item.template,
                    });
                  });
                },
                g => (g.template = group.template),
              ),
            );
          }
          list.push(category);
          categoryMap.set(categoryId, category);
        }

        return {
          categories: list,
          serviceSubjectItemMap: map,
          categoryMapById: categoryMap,
          groupMapById: groupMap,
        };
      }, [
        templates,
        vehicleInspectionSubjects,
        pendingIssues,
        constructionWorkItemCategories,
      ]);

    const onCustomServiceSubjectModalDismiss = useCallback(() => {
      showCustomServiceSubjectModal$.next(false);
    }, [showCustomServiceSubjectModal$]);

    const [showCustomServiceSubjectModal] = useBehaviorSubject(
      showCustomServiceSubjectModal$,
    );

    const performPostValidation = usePersistFn(() => {
      validateServiceSubjects?.();
    });

    useEffect(() => {
      return navigation.addListener('beforeRemove', () => {
        performPostValidation();
      });
    }, [navigation, performPostValidation]);

    return (
      <ServiceSubjectsContext.Provider
        value={{
          categories,
          categoryMapById,
          groupMapById,
          serviceSubjectItemMap,
          addServiceSubject,
          removeServiceSubject,
          updateServiceSubject,
        }}
      >
        {children}
        <SelectedServiceSubjectsBottomSheet
          action$={selectedSubjectsModalAction$}
          onDone={onDone}
        />
        <CustomServiceSubjectBottomSheetModal
          visible={showCustomServiceSubjectModal !== false}
          itemBeingEdited={
            showCustomServiceSubjectModal &&
            typeof showCustomServiceSubjectModal !== 'boolean' &&
            isFaultLikeServiceSubject(showCustomServiceSubjectModal)
              ? showCustomServiceSubjectModal
              : undefined
          }
          onDismiss={onCustomServiceSubjectModalDismiss}
        />
      </ServiceSubjectsContext.Provider>
    );
  },
);
